/*
  Sticky add-to-cart bar.

  Shows once the real Add to Cart button has scrolled out of view upward, and
  mirrors that button's state (label, disabled) plus the live price. The bar's
  button submits the real product form via its `form=` attribute, so Dawn's
  existing product-form handler does the AJAX add and opens the cart drawer —
  no duplicated form, no variant state to keep in sync.
*/

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) return;

    this.realButton = this.form.querySelector('.product-form__submit');
    this.realPrice = document.querySelector('.product__info-container .price');
    this.button = this.querySelector('[data-sticky-button]');
    this.label = this.querySelector('[data-sticky-label]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    if (!this.realButton || !this.button) return;

    this.visible = false;
    this.sync();

    // Reveal only after the real button has passed ABOVE the viewport. Without the
    // boundingClientRect check the bar also appears when the button is still below
    // the fold on load, which is exactly when it is not wanted.
    this.io = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(scrolledPast);
      },
      { threshold: 0 }
    );
    this.io.observe(this.realButton);

    // Dawn re-renders the buy box on variant change, so watch for label/disabled
    // and price changes rather than trying to recompute them here.
    this.mo = new MutationObserver(() => this.sync());
    this.mo.observe(this.realButton, { attributes: true, childList: true, subtree: true });
    if (this.realPrice) {
      this.mo.observe(this.realPrice, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.mo?.disconnect();
  }

  toggle(show) {
    if (show === this.visible) return;
    this.visible = show;
    if (show) {
      this.hidden = false;
      // Next frame so the transition has a starting state to animate from.
      requestAnimationFrame(() => this.classList.add('sticky-atc--visible'));
    } else {
      this.classList.remove('sticky-atc--visible');
      const done = () => {
        if (!this.visible) this.hidden = true;
        this.removeEventListener('transitionend', done);
      };
      // Fall back to hiding immediately when transitions are suppressed.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.hidden = true;
      } else {
        this.addEventListener('transitionend', done);
      }
    }
  }

  sync() {
    if (this.realButton.hasAttribute('disabled')) {
      this.button.setAttribute('disabled', '');
    } else {
      this.button.removeAttribute('disabled');
    }

    // The real button's first span holds the current label (Add to cart / Sold out /
    // Unavailable). Ignore the loading-spinner span.
    const realLabel = this.realButton.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
    if (realLabel && this.label) {
      const text = realLabel.textContent.trim();
      if (text) this.label.textContent = text;
    }

    // Clone the whole .price ELEMENT, not its innerHTML. Dawn hides the Sale and
    // Sold out badges with rules scoped under `.price`, so copying only the inner
    // markup into a plain div left those badges with nothing to match — and both
    // rendered on every product. Strip ids so nothing is duplicated in the DOM.
    if (this.realPrice && this.priceEl) {
      const clone = this.realPrice.cloneNode(true);
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      this.priceEl.replaceChildren(clone);
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
